/**
 * NASDAQ Quantum — renderer.
 *
 * Solo pinta: la detección vive en el motor (vía adapter.js). Todo el
 * dibujo pasa por createChartDrawingApi (addSeries / addCustomSeries /
 * addPriceLine / addLabel) -- nunca LWC directo ni DOM propio sobre el
 * chart (Standard C.2), y ninguna serie toca la escala de precios
 * (autoscaleInfoProvider: () => null).
 *
 * Elementos visuales (WP §1, desviación #3 -- lista cerrada, los únicos
 * que se pintan): caja del rango, línea EMA, marcadores LONG/SHORT en la
 * entrada, líneas de entry/SL/TP. Sin marcadores de resultado (WP §3: el
 * motor ni siquiera calcula ganadas/perdidas -- no hay nada de eso que
 * pintar, y no se inventa un elemento visual no pedido).
 *
 * Etiquetas en el eje (fix 2026-09-28): el `title` de una serie de líneas
 * se pinta junto al eje aunque `lastValueVisible` sea false, y entra en el
 * anti-solape nativo del eje. Solo lleva título el rango VIGENTE (el último
 * del motor); los históricos y las líneas de nivel van sin título. Las
 * etiquetas Entrada/TP/SL las pinta LevelLabelsPrimitive, ancladas a su
 * precio con prioridad Entrada > TP > SL.
 */

import { resolveClassParams, signalClassBadge } from '@/utils/signalClass'
import { LineStyle } from 'lightweight-charts'
import { ZoneFillSeries } from './ZoneFillSeries.js'
import { LevelLabelsPrimitive } from './LevelLabelsPrimitive.js'
import {
  QUANTUM_COLORS,
  ZONE_OPACITY,
  LABEL_FONT,
  resolveConfig,
  resolveColor,
  resolveColors,
  withAlpha,
} from './constants.js'

const MAX_RANGES_DRAWN = 15
const MAX_SIGNALS_DRAWN = 40

const COMMON_LINE_OPTS = {
  lastValueVisible: false,
  priceLineVisible: false,
  crosshairMarkerVisible: false,
  autoscaleInfoProvider: () => null,
}

function formatPrice(v) {
  if (!Number.isFinite(v)) return '—'
  if (Math.abs(v) >= 1000) return v.toFixed(1)
  if (Math.abs(v) >= 1) return v.toFixed(2)
  return v.toFixed(4)
}

function formatPct(entry, price) {
  if (!Number.isFinite(entry) || entry === 0 || !Number.isFinite(price)) return '—'
  const pct = ((price - entry) / entry) * 100
  return `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`
}

// Mismas guardas de pintado que PRQ (defensa en profundidad, hotfix P0
// 2026-08-03): lightweight-charts no valida síncronamente un custom series
// setData -- toda la validación de tiempo/precio es responsabilidad nuestra
// antes de cruzar cualquier llamada a api.*.
function isValidTime(t) {
  return t != null && t !== '' && !Number.isNaN(t)
}

function isValidPrice(v) {
  return Number.isFinite(v)
}

function isValidLinePoint(p) {
  return p != null && isValidTime(p.time) && isValidPrice(p.value)
}

function isValidZonePoint(p) {
  return p != null && isValidTime(p.time) && isValidPrice(p.upper) && isValidPrice(p.lower)
}

function addLine(api, title, color, lineWidth, lineStyle, points) {
  const clean = Array.isArray(points) ? points.filter(isValidLinePoint) : []
  if (clean.length === 0) return null
  const s = api.addSeries(c => c.addLineSeries({ title, color, lineWidth, lineStyle, ...COMMON_LINE_OPTS }))
  if (s) s.setData(clean)
  return s
}

function addZoneSeries(api, points, fillColor, chart) {
  const clean = (Array.isArray(points) ? points.filter(isValidZonePoint) : [])
    .sort((a, b) => (a.time < b.time ? -1 : a.time > b.time ? 1 : 0))
    .filter((p, i, arr) => i === 0 || p.time !== arr[i - 1].time)
  if (clean.length < 2) return
  const s = api.addCustomSeries(new ZoneFillSeries(), { color: 'rgba(0,0,0,0)', fillColor, lineWidth: 0, ...COMMON_LINE_OPTS })
  if (!s) return
  try {
    s.setData(clean.map(p => ({ time: p.time, upper: p.upper, lower: p.lower, color: p.color ?? fillColor })))
  } catch {
    try { chart?.removeSeries?.(s) } catch { /* noop */ }
  }
}

export function renderNasdaqQuantum(chart, candleSeries, result, config, api) {
  if (!result || !api) return
  const cfg = resolveConfig(config)
  if (cfg.enabled === false || result.gated) return

  const ranges = Array.isArray(result.ranges) ? result.ranges : []
  const signals = Array.isArray(result.signals) ? result.signals : []
  const emaSeries = Array.isArray(result.emaSeries) ? result.emaSeries : []
  if (ranges.length === 0 && signals.length === 0 && emaSeries.length === 0) return

  const colors = resolveColors(cfg)

  const bars = candleSeries && typeof candleSeries.data === 'function' ? candleSeries.data() : []
  const lastBar = Array.isArray(bars) && bars.length > 0 ? bars[bars.length - 1] : null
  const lastTime = lastBar?.time ?? null

  // ── Línea EMA(50) -- serie continua sobre todo el histórico cargado ──────
  if (cfg.showEmaLine && emaSeries.length > 0) {
    addLine(api, 'NQ EMA', colors.ema, 1, LineStyle.Solid, emaSeries)
  }

  const rangesToDraw = ranges.slice(-MAX_RANGES_DRAWN).filter(r => r.rangeHigh != null && r.rangeLow != null)
  // Rango vigente = el último que emite el motor (el del día en curso); si
  // aún no tiene niveles (ventana A sin cerrar), no hay rango que etiquetar.
  const currentRange = ranges.length > 0 ? ranges[ranges.length - 1] : null

  // ── Relleno de la caja del rango (por vela dentro del rango) ────────────
  if (cfg.showRangeFill) {
    for (const r of rangesToDraw) {
      const fillColor = withAlpha(colors.range, ZONE_OPACITY)
      const span = bars.filter(b => isValidTime(b.time) && b.time >= r.startTime && b.time <= r.endTime)
      const points = span.length >= 2 ? span : [
        { time: r.startTime, upper: r.rangeHigh, lower: r.rangeLow },
        { time: r.endTime, upper: r.rangeHigh, lower: r.rangeLow },
      ]
      addZoneSeries(
        api,
        points.map(p => ({ time: p.time, upper: r.rangeHigh, lower: r.rangeLow, color: fillColor })),
        fillColor,
        chart,
      )
    }
  }

  // ── Bordes de la caja del rango (líneas superior/inferior) ──────────────
  if (cfg.showRangeBox) {
    for (const r of rangesToDraw) {
      const title = r === currentRange ? 'NQ Rango' : ''
      addLine(api, title, colors.range, cfg.lineWidth, LineStyle.Solid, [
        { time: r.startTime, value: r.rangeHigh },
        { time: r.endTime, value: r.rangeHigh },
      ])
      addLine(api, title, colors.range, cfg.lineWidth, LineStyle.Solid, [
        { time: r.startTime, value: r.rangeLow },
        { time: r.endTime, value: r.rangeLow },
      ])
    }
  }

  // ── Marcadores LONG/SHORT en el precio de entrada ────────────────────────
  const signalsToDraw = signals.slice(-MAX_SIGNALS_DRAWN)
  // Clase A/A+ (WP A/A+ fase 2): misma fórmula que la tarjeta y mismo TP
  // (NQ es el único indicador cuya tarjeta usa los niveles de su propio
  // motor, _nq_card_signal_data) -- aquí chart y Telegram coinciden. Solo
  // etiqueta: A y A+ se pintan siempre.
  const classParams = resolveClassParams(cfg)
  if (cfg.showSignalLabels) {
    for (const sig of signalsToDraw) {
      if (!isValidTime(sig.time) || !Number.isFinite(sig.entry)) continue
      const isLong = sig.direction === 'LONG'
      const color = isLong ? colors.long : colors.short
      api.addLabel({
        time: sig.time,
        price: sig.entry,
        text: `${sig.direction} ${formatPrice(sig.entry)}`,
        color,
        bgColor: withAlpha(color, 0.12),
        boxStyle: 'flag',
        isBull: isLong,
        font: LABEL_FONT,
        signalClass: signalClassBadge(sig.entry, sig.tp, classParams),
      })
    }
  }

  // ── Niveles de la señal activa o, si no hay, la última (congelados) ─────
  const levelsSignal = result.activeSignal || result.lastSignal || null
  if (cfg.showLevels && levelsSignal && Number.isFinite(levelsSignal.entry) && isValidTime(levelsSignal.time)) {
    const { entry, sl, tp, time: sigTime, closeTime } = levelsSignal
    const endTime = levelsSignal.status === 'active'
      ? (lastTime != null && lastTime > sigTime ? lastTime : sigTime)
      : (isValidTime(closeTime) && closeTime > sigTime ? closeTime : sigTime)

    const levelLine = (price, color, width, style) => {
      if (!Number.isFinite(price)) return null
      return addLine(api, '', color, width, style, [
        { time: sigTime, value: price },
        ...(endTime !== sigTime ? [{ time: endTime, value: price }] : []),
      ])
    }
    const entrySeries = levelLine(entry, colors.entry, 1, LineStyle.Solid)
    levelLine(sl, colors.sl, 1.5, LineStyle.Solid)
    levelLine(tp, colors.tp, 1.5, LineStyle.Dashed)

    // Etiquetas de eje: la primitiva vive en la serie de la línea de entrada
    // (creada vía api → se elimina con ella en clearIndicator), pero mide
    // contra la serie de velas, que es la del eje. Orden = prioridad.
    const isLong = levelsSignal.direction === 'LONG'
    const levels = [
      { key: 'entry', price: entry, title: `Entrada ${formatPrice(entry)}`, color: colors.entry, preferDir: isLong ? 1 : -1 },
      { key: 'tp', price: tp, title: `TP ${formatPrice(tp)} (${formatPct(entry, tp)})`, color: colors.tp, preferDir: isLong ? -1 : 1 },
      { key: 'sl', price: sl, title: `SL ${formatPrice(sl)} (${formatPct(entry, sl)})`, color: colors.sl, preferDir: isLong ? 1 : -1 },
    ]
    if (entrySeries && typeof entrySeries.attachPrimitive === 'function') {
      // Obstáculos: las otras etiquetas de eje del propio indicador (EMA y
      // rango vigente, con título); el precio vivo lo lee la primitiva.
      const obstaclePrices = []
      if (cfg.showEmaLine && emaSeries.length > 0) obstaclePrices.push(emaSeries[emaSeries.length - 1]?.value)
      if (cfg.showRangeBox && currentRange && rangesToDraw.includes(currentRange)) {
        obstaclePrices.push(currentRange.rangeHigh, currentRange.rangeLow)
      }
      entrySeries.attachPrimitive(new LevelLabelsPrimitive(levels, { priceSeries: candleSeries, obstaclePrices }))
    }
  }
}
